import {Component, OnInit} from '@angular/core';
import {Router} from '@angular/router';
import {DynamicDialogConfig, DynamicDialogRef} from 'primeng/dynamicdialog';
import {take} from 'rxjs/operators';
import {UserModel} from 'src/app/models/User';
import {AuthService} from 'src/app/services/auth.service';
import {ViewportService} from 'src/app/services/viewport.service';
import {SubscriptionService} from '../../../services/subscription.service';
import {IntercomService} from '../../../services/intercom.service';
import {ToastrService} from 'ngx-toastr';
import {MixpanelService} from '../../../services/mixpanel.service';

type UpdateModalMode = 'initial' | 'success' | 'error';
export type PauseDurationMonths = 2 | 3;

interface PauseOffer {
    durationMonths: PauseDurationMonths;
    title: string;
    body: string[];
    ctaLabel: string;
    successTitle: string;
    successBody: string;
    errorTitle: string;
    errorBody: string;
}

@Component({
    selector: 'update-modal',
    templateUrl: './update-modal.component.html',
    styleUrl: './update-modal.component.css'
})
export class UpdateModalComponent implements OnInit {
    user!: UserModel;
    mode: UpdateModalMode = 'initial';
    isLoading = false;
    errorMessage: string | null = null;
    offer!: PauseOffer;

    constructor(
        public ref: DynamicDialogRef,
        public config: DynamicDialogConfig,
        public viewport: ViewportService,
        private router: Router,
        private auth: AuthService,
        private subscriptionService: SubscriptionService,
        public intercom: IntercomService,
        public toastr: ToastrService,
        private mixpanel: MixpanelService
    ) {}

    ngOnInit(): void {
        const user = this.config.data.user as UserModel;
        const pauseDurationMonths = (this.config.data.offerType as PauseDurationMonths | null) ?? 2;

        this.user = user;

        this.mixpanel.identify(user.id.toString());
        this.mixpanel.track('Pause modal opened', {pauseDurationMonths});

        this.offer = this.buildPauseOffer(pauseDurationMonths);
    }

    private buildPauseOffer(durationMonths: PauseDurationMonths): PauseOffer {
        return {
            durationMonths,
            title: 'Looking to update or pause your membership?',
            body: [
                `Taking a break from travel or need more time to explore? Pause your membership for ${durationMonths} months or update your plan anytime.`
            ],
            ctaLabel: 'Pause Plan',
            successTitle: "We've paused your account",
            successBody: `You’ve successfully paused your membership for ${durationMonths} months.`,
            errorTitle: 'You have previously paused your account',
            errorBody: 'Members are allowed 1 account pause. Please contact support with any questions.'
        };
    }

    primaryAction(): void {
        if (!this.offer) {
            return;
        }

        this.isLoading = true;
        this.errorMessage = null;

        this.subscriptionService.pausePlan(this.offer.durationMonths).pipe(take(1)).subscribe({
            next: () => {
                this.isLoading = false;
                this.mode = 'success';
            },
            error: (err) => {
                this.isLoading = false;
                this.toastr.error(
                    err.error.message,
                    'Issue pausing membership'
                );
                this.mode = 'error';
            }
        });
    }x

    close(): void {
        this.mixpanel.track('Extend or Pause modal closed', {
            pauseDuration: this.offer?.durationMonths
        });
        this.ref.close();
        this.router.navigate(['/account/settings'])
    }
}
